/* eslint-disable */
import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronDown,
  FileText,
  House,
  Megaphone,
  UserRound,
  UsersRound,
} from "lucide-react";
import "./ParentTabletLayout.css";

function getRouteTitle(pathname) {
  if (pathname.includes("/attendance")) return "Attendance";
  if (pathname.includes("/updates")) return "Updates";
  if (pathname.includes("/notifications")) return "Notifications";
  if (pathname.includes("/profile")) return "Profile";
  if (pathname.includes("/report-absence")) return "Report absence";
  if (pathname.includes("/reports")) return "Reports";
  return "Home";
}

function getRouteSubtitle(pathname) {
  if (pathname.includes("/attendance")) return "Attendance history and daily visibility";
  if (pathname.includes("/updates")) return "School notices, class updates and behaviour";
  if (pathname.includes("/notifications")) return "Recent alerts from your school";
  if (pathname.includes("/profile")) return "Security, preferences and linked children";
  if (pathname.includes("/report-absence")) return "Send absence details securely";
  if (pathname.includes("/reports")) return "Monthly attendance documents";
  return "Everything important in one calm space";
}

function TabletNavItem({ to, label, icon, badgeCount = 0 }) {
  return (
    <NavLink
      to={to}
      end={to === "/parent/home"}
      className={({ isActive }) =>
        `ppt-nav-item ${isActive ? "active" : ""}`
      }
    >
      <span className="ppt-nav-icon">
        {icon}
        {badgeCount > 0 ? (
          <span className="ppt-nav-badge">{badgeCount > 99 ? "99+" : badgeCount}</span>
        ) : null}
      </span>
      <span>{label}</span>
    </NavLink>
  );
}

export default function ParentTabletLayout({
  outletContext,
  childOptions = [],
  activeChild = null,
  activeChildId = "",
  onChildChange,
  unreadCount = 0,
  isOnline = true,
  liveToast = null,
  onToastOpen,
  onToastClose,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);

  const title = useMemo(() => getRouteTitle(location.pathname), [location.pathname]);
  const subtitle = useMemo(
    () => getRouteSubtitle(location.pathname),
    [location.pathname]
  );

  const resolvedChild =
    activeChild ||
    childOptions.find((child) => String(child?.id || "") === String(activeChildId || "")) ||
    childOptions[0] ||
    null;

  const childName = resolvedChild?.name || "Select child";
  const childYear =
    resolvedChild?.year ||
    resolvedChild?.yearGroup ||
    resolvedChild?.className ||
    "";

  function selectChild(child) {
    const id = child?.id || child?.studentId || child?.docId || "";
    if (id && typeof onChildChange === "function") {
      onChildChange(id);
    }
    setPickerOpen(false);
  }

  return (
    <div className="ppt-shell">
      <aside className="ppt-sidebar">
        <button
          type="button"
          className="ppt-brand"
          onClick={() => navigate("/parent/home")}
        >
          <img src="/presentplus-logo.png" alt="" />
          <span>
            <strong>PresentPlus</strong>
            <small>Parent Portal</small>
          </span>
        </button>

        <nav className="ppt-sidebar-nav" aria-label="Parent tablet navigation">
          <TabletNavItem to="/parent/home" label="Home" icon={<House size={22} />} />
          <TabletNavItem
            to="/parent/attendance"
            label="Attendance"
            icon={<CalendarDays size={22} />}
          />
          <TabletNavItem
            to="/parent/updates"
            label="Updates"
            icon={<Megaphone size={22} />}
            badgeCount={unreadCount}
          />
          <TabletNavItem
            to="/parent/reports"
            label="Reports"
            icon={<FileText size={22} />}
          />
          <TabletNavItem
            to="/parent/profile"
            label="Profile"
            icon={<UserRound size={22} />}
          />
        </nav>

        <div className="ppt-sidebar-foot">
          <span className={`ppt-online-dot ${isOnline ? "online" : "offline"}`} />
          <span>{isOnline ? "Connected" : "Offline"}</span>
        </div>
      </aside>

      <div className="ppt-workspace">
        <header className="ppt-header">
          <div className="ppt-heading">
            <p>Parent portal</p>
            <h1>{title}</h1>
            <span>{subtitle}</span>
          </div>

          <div className="ppt-header-actions">
            <div className="ppt-child-picker-wrap">
              <button
                type="button"
                className={`ppt-child-picker ${pickerOpen ? "open" : ""}`}
                onClick={() => setPickerOpen((current) => !current)}
                disabled={!childOptions.length}
              >
                <span className="ppt-child-avatar">
                  <UsersRound size={20} />
                </span>
                <span className="ppt-child-copy">
                  <small>Viewing child</small>
                  <strong>{childName}</strong>
                  {childYear ? <em>{childYear}</em> : null}
                </span>
                <ChevronDown size={19} />
              </button>

              {pickerOpen ? (
                <div className="ppt-child-menu">
                  <div className="ppt-child-menu-head">
                    <strong>Choose child</strong>
                    <span>Switch the dashboard context</span>
                  </div>

                  {childOptions.map((child) => {
                    const id = child?.id || child?.studentId || child?.docId || "";
                    const selected = String(id) === String(activeChildId || resolvedChild?.id || "");
                    const year =
                      child?.year ||
                      child?.yearGroup ||
                      child?.className ||
                      "Year group";

                    return (
                      <button
                        type="button"
                        key={id}
                        className={`ppt-child-option ${selected ? "selected" : ""}`}
                        onClick={() => selectChild(child)}
                      >
                        <span className="ppt-child-avatar">
                          <UsersRound size={19} />
                        </span>
                        <span>
                          <strong>{child?.name || "Child"}</strong>
                          <small>{year}</small>
                        </span>
                        {selected ? <em>Current</em> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {!isOnline ? (
          <div className="ppt-offline-banner">
            You are offline. Some information may not be up to date.
          </div>
        ) : null}

        <main className="ppt-main">
          <div className="ppt-content">
            <Outlet context={{ ...outletContext, isTabletLayout: true }} />
          </div>
        </main>
      </div>

      <nav className="ppt-bottom-nav" aria-label="Parent tablet portrait navigation">
        <TabletNavItem to="/parent/home" label="Home" icon={<House size={20} />} />
        <TabletNavItem
          to="/parent/attendance"
          label="Attendance"
          icon={<CalendarDays size={20} />}
        />
        <TabletNavItem
          to="/parent/updates"
          label="Updates"
          icon={<Megaphone size={20} />}
          badgeCount={unreadCount}
        />
        <TabletNavItem
          to="/parent/reports"
          label="Reports"
          icon={<FileText size={20} />}
        />
        <TabletNavItem
          to="/parent/profile"
          label="Profile"
          icon={<UserRound size={20} />}
        />
      </nav>

      {liveToast ? (
        <button
          type="button"
          className="ppt-live-toast"
          onClick={() => {
            onToastOpen?.(liveToast);
            onToastClose?.();
          }}
        >
          <span />
          <div>
            <strong>{liveToast.title}</strong>
            <small>{liveToast.body}</small>
          </div>
        </button>
      ) : null}
    </div>
  );
}
